import { CalendarDays, FileSearch, Search, X } from 'lucide-react'
import { FilterSearchDropdown } from '@/components/rfid/FilterSearchDropdown'
import type {
  DocumentListFilterState,
  FilterOption,
} from '@/utils/documentListFilters'
import { hasActiveDocumentListFilters } from '@/utils/documentListFilters'
import { cn } from '@/utils/cn'

type Props = {
  filters: DocumentListFilterState
  onChange: (patch: Partial<DocumentListFilterState>) => void
  onClear: () => void
  customerOptions: FilterOption[]
  plantOptions: FilterOption[]
  resultCount: number
}

export function DocumentListToolbar({
  filters,
  onChange,
  onClear,
  customerOptions,
  plantOptions,
  resultCount,
}: Props) {
  const active = hasActiveDocumentListFilters(filters)

  return (
    <section className="rfid-doc-filters" aria-label="Document filters">
      <div className="rfid-doc-filters__grid">
        <label className="rfid-filter-field">
          <span className="rfid-filter-field__label">Doc #</span>
          <span
            className={cn(
              'rfid-filter-field__control-wrap',
              filters.filterDocNo && 'rfid-filter-field__control-wrap--active'
            )}
          >
            <FileSearch size={15} strokeWidth={2.4} className="rfid-filter-field__icon" />
            <input
              type="search"
              className="rfid-filter-field__control"
              placeholder="e.g. 3020012284"
              value={filters.filterDocNo}
              onChange={(e) => onChange({ filterDocNo: e.target.value })}
              aria-label="Filter by document number"
            />
          </span>
        </label>

        <FilterSearchDropdown
          label="Customer"
          placeholder="All customers"
          value={filters.filterCustomer}
          options={customerOptions}
          onChange={(v) => onChange({ filterCustomer: v })}
          disabled={customerOptions.length === 0}
        />

        <FilterSearchDropdown
          label="Plant"
          placeholder="All plants"
          value={filters.filterPlant}
          options={plantOptions}
          onChange={(v) => onChange({ filterPlant: v })}
          disabled={plantOptions.length === 0}
        />

        <label className="rfid-filter-field">
          <span className="rfid-filter-field__label">Date</span>
          <span
            className={cn(
              'rfid-filter-field__control-wrap',
              filters.filterDate && 'rfid-filter-field__control-wrap--active'
            )}
          >
            <CalendarDays size={15} strokeWidth={2.4} className="rfid-filter-field__icon" />
            <input
              type="date"
              className="rfid-filter-field__control rfid-filter-field__control--date"
              value={filters.filterDate}
              onChange={(e) => onChange({ filterDate: e.target.value })}
              aria-label="Filter by posting date"
            />
          </span>
        </label>
      </div>

      <div className="rfid-doc-filters__search-row">
        <label className="rfid-filter-search">
          <Search size={16} strokeWidth={2.4} className="rfid-filter-search__icon" />
          <input
            type="search"
            className="rfid-filter-search__input"
            placeholder="Search all fields…"
            value={filters.search}
            onChange={(e) => onChange({ search: e.target.value })}
            aria-label="Search all fields"
          />
        </label>

        <button
          type="button"
          className={cn(
            'rfid-doc-filters__clear',
            active && 'rfid-doc-filters__clear--active'
          )}
          onClick={onClear}
          disabled={!active}
        >
          <X size={14} strokeWidth={2.6} />
          Clear
        </button>

        <div className="rfid-doc-filters__count" aria-live="polite">
          <strong>{resultCount}</strong>
          <span>document{resultCount === 1 ? '' : 's'}</span>
        </div>
      </div>
    </section>
  )
}
